/** Shared {x,y,zoom} framing contract used by anear-image-upload and merchant placard PDF. */

export const DEFAULT_IMAGE_POSITION = { x: 50, y: 50, zoom: 1 }

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n))
}

export function normalizeImagePosition(raw, fallback = DEFAULT_IMAGE_POSITION) {
  if (!raw) return { ...fallback }

  let parsed = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return { ...fallback }
    }
  }

  const x = Number(parsed?.x)
  const y = Number(parsed?.y)
  const zoom = Number(parsed?.zoom)
  return {
    x: Number.isFinite(x) ? clamp(x, 0, 100) : fallback.x,
    y: Number.isFinite(y) ? clamp(y, 0, 100) : fallback.y,
    zoom: Number.isFinite(zoom) ? clamp(zoom, 1, 3) : fallback.zoom
  }
}

/** Returns null when framing is absent/invalid (PDF then uses theme defaults). */
export function normalizeOptionalImagePosition(raw) {
  if (raw == null || raw === '') return null
  if (typeof raw === 'string' && !raw.trim()) return null

  let parsed = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!parsed || typeof parsed !== 'object') return null

  const x = Number(parsed.x)
  const y = Number(parsed.y)
  const zoom = Number(parsed.zoom)
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom)) return null

  return {
    x: Math.round(clamp(x, 0, 100) * 10) / 10,
    y: Math.round(clamp(y, 0, 100) * 10) / 10,
    zoom: Math.round(clamp(zoom, 1, 3) * 100) / 100
  }
}

export function serializeImagePosition(position) {
  const normalized = normalizeImagePosition(position)
  return JSON.stringify({
    x: Math.round(normalized.x * 10) / 10,
    y: Math.round(normalized.y * 10) / 10,
    zoom: Math.round(normalized.zoom * 100) / 100
  })
}

/**
 * Source-image percentages for the window react-easy-crop should open on.
 *
 * width% and height% match the crop aspect against the photo. A square box
 * (width === height) is only right when the photo and the mask share an aspect;
 * otherwise the editor pins a taller photo to its top edge and under-zooms a
 * wider one. Omitting the frame keeps that square box for callers that have
 * not measured the file yet.
 */
export function initialCropArea(position, zoom, frame = {}) {
  const normalized = normalizeImagePosition(position)
  const safeZoom = Math.max(zoom || normalized.zoom || 1, 1)
  const imageWidth = Number(frame.imageWidth)
  const imageHeight = Number(frame.imageHeight)
  const cropAspect = Number(frame.aspect)
  const imageAspect = imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : 0
  const hasFrame = imageAspect > 0 && cropAspect > 0

  let width
  let height
  if (!hasFrame) {
    width = 100 / safeZoom
    height = width
  } else if (imageAspect <= cropAspect) {
    width = 100 / safeZoom
    height = (imageAspect / cropAspect) * width
  } else {
    height = 100 / safeZoom
    width = (cropAspect / imageAspect) * height
  }

  width = clamp(width, 1, 100)
  height = clamp(height, 1, 100)
  return {
    x: clamp(normalized.x - width / 2, 0, 100 - width),
    y: clamp(normalized.y - height / 2, 0, 100 - height),
    width,
    height
  }
}

export function positionFromCroppedArea(croppedArea, zoom) {
  return {
    x: clamp(croppedArea.x + croppedArea.width / 2, 0, 100),
    y: clamp(croppedArea.y + croppedArea.height / 2, 0, 100),
    zoom
  }
}

/**
 * CSS for displaying a crop-mode="position" frame.
 *
 * Stored {x,y} is the focal point as % of the source image (crop-center from
 * react-easy-crop). object-position anchors that focal point; scale(zoom) around
 * the same origin matches the editor slider. Do not also translate by (50-x, 50-y):
 * that double-shifts with object-position and over-crops (regression in 4f65433).
 */
export function imagePositionPreviewStyle(position) {
  const normalized = normalizeOptionalImagePosition(position)
  if (!normalized) return undefined
  const { x, y, zoom } = normalized
  return {
    objectFit: 'cover',
    objectPosition: `${x}% ${y}%`,
    transform: `scale(${zoom})`,
    transformOrigin: `${x}% ${y}%`
  }
}

/** Inline style string for pug / server-rendered img tags. */
export function imagePositionCssText(position) {
  const style = imagePositionPreviewStyle(position)
  if (!style) return ''
  return [
    `object-fit: ${style.objectFit}`,
    `object-position: ${style.objectPosition}`,
    `transform: ${style.transform}`,
    `transform-origin: ${style.transformOrigin}`
  ].join('; ')
}

/** Apply framing styles to an HTMLImageElement (or clear when absent). */
export function applyImagePositionToElement(img, position) {
  if (!img) return
  const style = imagePositionPreviewStyle(position)
  if (!style) {
    img.style.objectFit = ''
    img.style.objectPosition = ''
    img.style.transform = ''
    img.style.transformOrigin = ''
    return
  }
  img.style.objectFit = style.objectFit
  img.style.objectPosition = style.objectPosition
  img.style.transform = style.transform
  img.style.transformOrigin = style.transformOrigin
}
